import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  RiCheckLine,
  RiArrowDownSLine,
  RiArrowUpSLine,
} from "@remixicon/react";
import { cn } from "../../../lib/utils";
import { SearchableDropdown } from "../SearchableDropdown";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

function SelectTrigger({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>;
}) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-stroke-secondary bg-bg-primary px-3 py-2 text-sm text-text-primary transition-colors",
        "hover:border-stroke-primary focus:outline-none focus:ring-1 focus:ring-stroke-primary",
        "disabled:cursor-not-allowed disabled:bg-bg-disabled disabled:text-text-disabled [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <RiArrowDownSLine className="size-4 text-icon-secondary" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

function SelectScrollUpButton({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollUpButton>>;
}) {
  return (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <RiArrowUpSLine className="size-4 text-icon-secondary" />
    </SelectPrimitive.ScrollUpButton>
  );
}
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

function SelectScrollDownButton({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollDownButton>>;
}) {
  return (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <RiArrowDownSLine className="size-4 text-icon-secondary" />
    </SelectPrimitive.ScrollDownButton>
  );
}
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

function SelectContent({
  className,
  children,
  position = "popper",
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Content>>;
}) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-stroke-secondary bg-bg-primary text-text-primary shadow-[0px_4px_8px_0px_rgba(0,0,0,0.12)]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}
SelectContent.displayName = SelectPrimitive.Content.displayName;

function SelectLabel({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Label>>;
}) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn("px-2 py-1.5 text-sm font-semibold", className)}
      {...props}
    />
  );
}
SelectLabel.displayName = SelectPrimitive.Label.displayName;

function SelectItem({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Item>>;
}) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded p-1 text-base leading-5 text-text-primary outline-none transition-colors",
        "hover:bg-bg-hover focus:bg-bg-hover",
        "data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <span className="ml-auto flex items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <RiCheckLine className="size-6 text-icon-blue" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}
SelectItem.displayName = SelectPrimitive.Item.displayName;

function SelectSeparator({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
  ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Separator>>;
}) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 my-1 h-px bg-stroke-secondary", className)}
      {...props}
    />
  );
}
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// Searchable Select Component - Uses SearchableDropdown internally
export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  options: SearchableSelectOption[];
  /** Label displayed above the selected value */
  label?: string;
  /** Placeholder text shown when no option is selected */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Error state */
  error?: boolean;
  /** Error message displayed below the select */
  errorMessage?: string;
  /** Whether to show search input in dropdown */
  showSearch?: boolean;
}

/**
 * SearchableSelect - A select component with search functionality and floating label.
 * Uses SearchableDropdown internally with smart positioning (avoidCollisions enabled).
 *
 * Floating label behavior:
 * - When empty and not focused: Shows placeholder centered
 * - When focused or has value: Label floats up, value shown below
 * - Border is secondary when empty, dark when populated or focused
 */
function SearchableSelect({
  value,
  onValueChange,
  options,
  label,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  error = false,
  errorMessage,
  showSearch = true,
}: SearchableSelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <SearchableDropdown
        value={value}
        onValueChange={onValueChange}
        options={options}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        disabled={disabled}
        // Make wrapper full width by default
        className={cn("w-full", className)}
        contentClassName={contentClassName}
        showSearch={showSearch}
        // Smart positioning - dropdown will auto-adjust position based on viewport
        avoidCollisions={true}
        renderTrigger={({ open, selectedOption, disabled: isDisabled }) => {
          const hasValue = !!selectedOption;
          const isActive = open || hasValue;

          return (
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-invalid={error}
              disabled={isDisabled}
              className={cn(
                // Base styles - using capsule-preset colors
                "flex h-12 w-full items-center justify-between gap-2",
                "rounded-lg border px-3 py-2",
                "transition-colors",
                // Border states
                error
                  ? "border-stroke-critical bg-bg-critical"
                  : isActive
                    ? "border-stroke-primary bg-bg-primary"
                    : "border-stroke-secondary bg-bg-primary",
                // Hover state (only when not in error and not active)
                !error && !isActive && "hover:border-stroke-primary",
                // Focus state
                !error && "focus:outline-none focus:border-stroke-primary",
                // Disabled state
                "disabled:cursor-not-allowed disabled:bg-bg-disabled disabled:text-text-disabled",
                triggerClassName,
              )}
            >
              <div
                className={cn(
                  "flex flex-1 flex-col items-start min-w-0",
                  !isActive && "justify-center",
                )}
              >
                {/* Floating label - shown when active (focused or has value) */}
                {label && isActive && (
                  <span className="text-xs font-normal uppercase leading-4 text-text-tertiary">
                    {label}
                  </span>
                )}
                {/* Value or placeholder */}
                {selectedOption ? (
                  <span className="truncate text-sm font-normal leading-5 text-text-primary">
                    {selectedOption.label}
                  </span>
                ) : (
                  <span className="truncate text-sm font-normal leading-5 text-text-tertiary">
                    {/* Show label as placeholder when not active, otherwise show placeholder */}
                    {isActive ? placeholder : label || placeholder}
                  </span>
                )}
              </div>
              <RiArrowDownSLine
                className={cn(
                  "size-5 shrink-0 text-icon-secondary transition-transform",
                  open && "rotate-180",
                )}
              />
            </button>
          );
        }}
      />
      {error && errorMessage && (
        <p className="px-4 text-xs leading-4 text-text-critical">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
SearchableSelect.displayName = "SearchableSelect";

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SearchableSelect,
};
